import lodash from 'lodash';
import { queryGraphQL, mutateGraphQL } from 'common/util/query';
import { aliasNodeWithArgs, nodeWithArgs, node, toQuery, enumeration, toMutation } from 'common/util/queryHelper';
import { addApplicationMessage } from 'common/actions/applicationMessagesActions';

export const ACTIONS = {
    SET_DASHBOARD_DATA: 'SET_DASHBOARD_DATA',
    LOADED_DASHBOARD_DATA: 'LOADED_DASHBOARD_DATA',
    LOADING_DASHBOARD_DATA: 'LOADING_DASHBOARD_DATA',
    SET_DASHBOARD_BINDER_STUDIES: 'SET_DASHBOARD_BINDER_STUDIES',
    SET_DASHBOARD_BINDER_STUDIES_ADMIN_BINDERS: 'SET_DASHBOARD_BINDER_STUDIES_ADMIN_BINDERS',
    SET_DASHBOARD_BINDER_STUDIES_TASKS: 'SET_DASHBOARD_BINDER_STUDIES_TASKS',
    SET_STUDY_BINDER_QUERY_COUNT: 'SET_STUDY_BINDER_QUERY_COUNT',
    SET_ADMIN_BINDER_QUERY_COUNT: 'SET_ADMIN_BINDER_QUERY_COUNT',
    LOADED_DASHBOARD_BINDER_STUDIES: 'LOADED_DASHBOARD_BINDER_STUDIES',
    LOADING_DASHBOARD_BINDER_STUDIES: 'LOADING_DASHBOARD_BINDER_STUDIES',
    LOADED_DASHBOARD_BINDER_STUDIES_ADMIN_BINDERS: 'LOADED_DASHBOARD_BINDER_STUDIES_ADMIN_BINDERS',
    LOADING_DASHBOARD_BINDER_STUDIES_ADMIN_BINDERS: 'LOADING_DASHBOARD_BINDER_STUDIES_ADMIN_BINDERS',
    SET_PINNED_BINDERS: 'SET_PINNED_BINDERS',
    SET_PENDING_TASKS: 'SET_PENDING_TASKS',
    SET_UNFILED_DOCUMENTS_COUNT: 'SET_UNFILED_DOCUMENTS_COUNT',
    LOADING_UNFILED_DOCS_COUNT: 'LOADING_UNFILED_DOCS_COUNT',
    LOADING_PENDING_TASKS: 'LOADING_PENDING_TASKS',
    CLEAR_DASHBOARD_DATA: 'CLEAR_DASHBOARD_DATA',
    SET_BINDER_CUSTOM_COLUMNS: 'SET_BINDER_CUSTOM_COLUMNS',
};

const getBindersByStatus = (status) => {
    return aliasNodeWithArgs(`${status.toLowerCase()}Binders`, 'binders', {
        statusFilter: enumeration(status),
    }, [
        'status',
        'id',
        'title',
        'description',
        'createdAt',
        'trialType',
        'primaryInvestigator',
        'irbNumber',
        'contact',
        'trialId',
        'site:departmentSiteDg',
        'ctGovNumber',
        'sponsorCro',
        'renewalDate',
        // 'myPermissions',
        node('createdBy', [
            'displayName',
            'emailAddress'
        ]),
        node('institution', [
            'id',
            'displayName',
        ])
    ]);
};

export function getDashboardDetails() {
    return async (dispatch) => {
        dispatch(getDashboardData());
        dispatch(getPinnedBinders());
    };
}

export function getDashboardDetailsBinderStudies(sortField, sortOrder, queryText, filters, binderLimit, binderOffset, binderCategory) {
    return async (dispatch) => {
        dispatch(getDashboardBinderStudies(sortField, sortOrder, queryText, filters, binderLimit, binderOffset, binderCategory));
        dispatch(getPinnedBinders());
    };
}

export function getDashboardDetailsBinderStudiesAdminBinders(sortField, sortOrder, queryText, filters, binderLimit, binderOffset, binderCategory) {
    return async (dispatch) => {
        dispatch(getDashboardBinderStudiesAdminBinders(sortField, sortOrder, queryText, filters, binderLimit, binderOffset, binderCategory));
        dispatch(getPinnedBinders());
    };
}

export function getDashboardDetailsBinderStudiesTasks(sortField, sortOrder, queryText, filters, binderLimit, binderOffset, binderCategory) {
    return async (dispatch) => {
        dispatch(getDashboardBinderStudiesTasks(sortField, sortOrder, queryText, filters, binderLimit, binderOffset, binderCategory));
        dispatch(getPinnedBinders());
    };
}

export function getDashboardData() {
    return async (dispatch, getState) => {

        dispatch(loadingDashboardData());
        const results = await queryGraphQL(toQuery([
            getBindersByStatus('NEW'),
            getBindersByStatus('ACTIVE'),
            getBindersByStatus('ARCHIVED')
        ]));

        if (results.errors) {
            dispatch(addApplicationMessage('error', 'Could not load binder data'));
        } else {
            dispatch(setDashboardData(results.data));
        }
        dispatch(loadedDashboardData());

    };
}

export function handleBinderCustomColumns(userId, institutionId, columns) {
    return async (dispatch) => {
        const mutationNode = nodeWithArgs('createBinderCustomColumns', {
            userId,
            institutionId,
            columns,
        });

        const mutation = toMutation(mutationNode);

        return mutateGraphQL(mutation).then(results => {
            if (results.data) {
                dispatch(getBinderCustomColumns(userId, institutionId));
            }
        });

    };
}

export function getBinderCustomColumns(userId, institutionId) {
    return async (dispatch) => {
        const binderCustomColumns = nodeWithArgs('customColumns', { institutionId, userId }, [
            node('custom_columns', [
                'label',
                'key',
                'checked'
            ])
        ]);

        queryGraphQL(toQuery(binderCustomColumns)).then(res => {
            if (res.data) {
                dispatch({
                    type: ACTIONS.SET_BINDER_CUSTOM_COLUMNS,
                    payload: res.data.customColumns,
                });
            }
        });

    };
}

export function getDashboardBinderStudies(sortField, sortOrder, queryText, filters, binderLimit, binderOffset, binderCategory) {
    //limit is set to 999999999 to fetch all studies and binders, if no limit is passed it returns only 10 records.
    //Needs a better approach, will be implemented in future.
    return async (dispatch, getState) => {
        dispatch(loadingDashboardBinderStudies());
        const storeState = getState();
        //maintain a local redux state to store all API calls and use latest one to map the data
        const queryCount = lodash.get(storeState, 'dashboard.studyBinderQueryCount', []);
        const randomNumber = Math.floor(Math.random() * 1000000) + 1;
        queryCount.push(randomNumber);
        dispatch(setDashboardStudyBinderQueryCount(queryCount));
        const binderStudiesQuery = nodeWithArgs('studiesAndBinders', {
            limit: 999999999,
            offset: 0,
            sortOrder: sortOrder,
            sortField: sortField,
            binderLimit: binderLimit,
            binderOffset: binderOffset,
            searchText: queryText,
            binderCategory: binderCategory,
            queryCount: randomNumber,
            ...filters
        }, [
            'sponsors',
            'siteNumbers',
            'binderTypes',
            'contacts',
            'principalInvestigators',
            'sites',
            'cros',
            'departments',
            'diseaseGroups',
            'totalCount',
            'queryCount',
            node('studiesAndBinders', [
                'id',
                'studyId',
                'name',
                'sponsorCro',
                'trialId',
                'binderType',
                'unfiledDocumentsCount',
                'pendingTasksCount',
                'activationDate',
                'contact',
                'ctGovNumber',
                'departmentSiteDg',
                'primaryInvestigator',
                'irbNumber',
                'createdAt',
                'renewalDate',
                'status',
                'binderId',
                'userHasAccess',
                'institutionId',
                'binderPermissions',
                'isStudy',
                'studyTypeName',
                'currentStageName',
                'category',
                'department',
                'site',
                'diseaseGroup',
                'cro',
                'sponsor',
                node('binderTemplate', [
                    'displayName',
                    'binderType',
                ]),
            ])
        ]);

        const results = await queryGraphQL(toQuery(binderStudiesQuery));
        const latestReduxCount = lodash.get(storeState, 'dashboard.studyBinderQueryCount', []);
        const returnedAPICount = results.data.studiesAndBinders.queryCount;

        // some studies do not have a binder template associated with them so chili throws an error and also returns the data
        // so ignore this error and move ahead, binderTemplate will be shown as -- in the UI if it is not found.
        if (results.errors && results.errors[0].message !== 'WHERE parameter "id" has invalid "undefined" value') {
            dispatch(addApplicationMessage('error', 'Could not load binder data'));
        } else {
            if ((latestReduxCount[latestReduxCount.length - 1] === returnedAPICount) || (latestReduxCount.length === 0)) {
                dispatch(setDashboardBinderStudies(results.data.studiesAndBinders));
                dispatch(setDashboardStudyBinderQueryCount([]));
            }
        }
        if ((latestReduxCount[latestReduxCount.length - 1] === returnedAPICount) || (latestReduxCount.length === 0)) {
            dispatch(loadedDashboardBinderStudies());
        }
    };
}

export function getDashboardBinderStudiesAdminBinders(sortField, sortOrder, queryText, filters, binderLimit, binderOffset, binderCategory) {
    //limit is set to 999999999 to fetch all studies and binders, if no limit is passed it returns only 10 records.
    //Needs a better approach, will be implemented in future.
    return async (dispatch, getState) => {
        dispatch(loadingDashboardBinderStudiesAdminBinders());
        const storeState = getState();
        //maintain a local redux state to store all API calls and use latest one to map the data
        const queryCount = lodash.get(storeState, 'dashboard.adminBinderQueryCount',[]);
        const randomNumber = Math.floor(Math.random() * 1000000) + 1;
        dispatch(setDashboardAdminBinderQueryCount(queryCount));
        queryCount.push(randomNumber);
        const binderStudiesQuery = nodeWithArgs('studiesAndBinders', {
            limit: 999999999,
            offset: 0,
            sortOrder: sortOrder,
            sortField: sortField,
            binderLimit: binderLimit,
            binderOffset: binderOffset,
            searchText: queryText,
            binderCategory: binderCategory,
            queryCount: randomNumber,
            ...filters
        }, [
            'sponsors',
            'siteNumbers',
            'binderTypes',
            'contacts',
            'principalInvestigators',
            'sites',
            'cros',
            'departments',
            'diseaseGroups',
            'totalCount',
            'queryCount',
            node('studiesAndBinders', [
                'id',
                'studyId',
                'name',
                'sponsorCro',
                'trialId',
                'binderType',
                'unfiledDocumentsCount',
                'pendingTasksCount',
                'activationDate',
                'contact',
                'ctGovNumber',
                'departmentSiteDg',
                'primaryInvestigator',
                'irbNumber',
                'createdAt',
                'renewalDate',
                'status',
                'binderId',
                'userHasAccess',
                'institutionId',
                'binderPermissions',
                'isStudy',
                'studyTypeName',
                'currentStageName',
                'category',
                'department',
                'site',
                'diseaseGroup',
                'cro',
                'sponsor',
                node('binderTemplate', [
                    'displayName',
                    'binderType',
                ]),
            ])
        ]);

        const results = await queryGraphQL(toQuery(binderStudiesQuery));
        const latestReduxCount = lodash.get(storeState, 'dashboard.adminBinderQueryCount', []);
        const returnedAPICount = results.data.studiesAndBinders.queryCount;

        // some studies do not have a binder template associated with them so chili throws an error and also returns the data
        // so ignore this error and move ahead, binderTemplate will be shown as -- in the UI if it is not found.
        if (results.errors && results.errors[0].message !== 'WHERE parameter "id" has invalid "undefined" value') {
            dispatch(addApplicationMessage('error', 'Could not load binder data'));
        } else {
            if ((latestReduxCount[latestReduxCount.length - 1] === returnedAPICount) || (latestReduxCount.length === 0)) {
                dispatch(setDashboardBinderStudiesAdminBinders(results.data.studiesAndBinders));
                dispatch(setDashboardAdminBinderQueryCount([]));
            }
        }
        if ((latestReduxCount[latestReduxCount.length - 1] === returnedAPICount) || (latestReduxCount.length === 0)) {
            dispatch(loadedDashboardBinderStudiesAdminBinders());
        }
    };
}



export function getDashboardBinderStudiesTasks(sortField, sortOrder, queryText, filters, binderLimit, binderOffset, binderCategory) {
    //limit is set to 999999999 to fetch all studies and binders, if no limit is passed it returns only 10 records.
    //Needs a better approach, will be implemented in future.
    return async (dispatch, getState) => {
        const binderStudiesQuery = nodeWithArgs('studiesAndBinders', {
            limit: 999999999,
            offset: 0,
            sortOrder: sortOrder,
            sortField: sortField,
            binderLimit: binderLimit,
            binderOffset: binderOffset,
            searchText: queryText,
            binderCategory: binderCategory,
            ...filters
        }, [
            node('studiesAndBinders', [
                'id',
                'name',
                'studyId',
                'isStudy',
                'binderId',
                'status',
            ])
        ]);

        dispatch(loadingDashboardBinderStudies());
        const results = await queryGraphQL(toQuery(binderStudiesQuery));

        // some studies do not have a binder template associated with them so chili throws an error and also returns the data
        // so ignore this error and move ahead, binderTemplate will be shown as -- in the UI if it is not found.
        if (results.errors && results.errors[0].message !== 'WHERE parameter "id" has invalid "undefined" value') {
            dispatch(addApplicationMessage('error', 'Could not load binder data'));
        } else {
            dispatch(setDashboardBinderStudiesTasks(results.data.studiesAndBinders));
        }
        dispatch(loadedDashboardBinderStudies());
    };
}



export function loadingUnfiledDocsCount(isLoading) {
    return async (dispatch) => {
        dispatch({
            type: ACTIONS.LOADING_UNFILED_DOCS_COUNT,
            payload: isLoading
        });
    };
}

export function getBindersUnfiledDocsCount() {
    return async (dispatch, getState) => {

        const storeState = getState();
        const binders = lodash.get(storeState, 'dashboard.binders', []);
        if (lodash.isEmpty(binders)) {
            return;
        }

        dispatch(loadingUnfiledDocsCount(true));
        const binderIds = binders.map(binder => binder.id);

        const query = nodeWithArgs('getUnfiledDocsCountByBinderId', {
            binderIds
        }, [
            'binderId',
            'unfiledDocsCount',
        ]);

        const result = await queryGraphQL(toQuery(query));
        if (result && result.errors) {
            dispatch(addApplicationMessage('error', 'Failed to retrieve unfiled documents count'));
        } else {
            dispatch({
                type: ACTIONS.SET_UNFILED_DOCUMENTS_COUNT,
                payload: result.data.getUnfiledDocsCountByBinderId
            });
        }
        dispatch(loadingUnfiledDocsCount(false));
    };
}

export function getPendingSignatureCount() {
    return async (dispatch, getState) => {
        const storeState = getState();
        const binders = lodash.get(storeState, 'dashboard.binders', []);

        if (lodash.isEmpty(binders)) {
            return;
        }

        dispatch(loadingPendingTasks(true));
        const binderIds = binders.map(binder => binder.id);

        const query = nodeWithArgs('getPendingSignatureCountByBinderIds', {
            binderIds
        }, [
            'binderId',
            'taskCount',
        ]);

        const result = await queryGraphQL(toQuery(query));

        if (result && result.errors) {
            dispatch(addApplicationMessage('error', 'Failed to retrieve pending tasks.'));
        } else {
            dispatch({
                type: ACTIONS.SET_PENDING_TASKS,
                payload: result.data.getPendingSignatureCountByBinderIds,
            });
        }
        dispatch(loadingPendingTasks(false));
    };
}

export function getPinnedBinders() {
    return async (dispatch, getState) => {
        const pinnedBinderQuery = node('getUserPinnedBinders');

        const results = await queryGraphQL(toQuery(pinnedBinderQuery));

        if (results.errors) {
            dispatch(addApplicationMessage('error', 'Failed to retrieve pinned binder preferences.'));
        } else {
            dispatch(setPinnedBinders(results.data.getUserPinnedBinders));
        }
    };
}
export function loadingPendingTasks(isLoading) {
    return async (dispatch) => {
        dispatch({
            type: ACTIONS.LOADING_PENDING_TASKS,
            payload: isLoading
        });
    };
}

export function setPinnedBinders(binderIds) {
    return {
        type: ACTIONS.SET_PINNED_BINDERS,
        payload: { binderIds, }
    };
}

export function setDashboardData(binders) {
    return {
        type: ACTIONS.SET_DASHBOARD_DATA,
        payload: binders
    };
}
export function setDashboardBinderStudies(binders) {
    return {
        type: ACTIONS.SET_DASHBOARD_BINDER_STUDIES,
        payload: binders
    };
}
export function setDashboardBinderStudiesAdminBinders(binders) {
    return {
        type: ACTIONS.SET_DASHBOARD_BINDER_STUDIES_ADMIN_BINDERS,
        payload: binders
    };
}
export function setDashboardBinderStudiesTasks(binders) {
    return {
        type: ACTIONS.SET_DASHBOARD_BINDER_STUDIES_TASKS,
        payload: binders
    };
}

export function setDashboardStudyBinderQueryCount(queryCount) {
    return {
        type: ACTIONS.SET_STUDY_BINDER_QUERY_COUNT,
        payload: queryCount
    };
}

export function setDashboardAdminBinderQueryCount(queryCount) {
    return {
        type: ACTIONS.SET_ADMIN_BINDER_QUERY_COUNT,
        payload: queryCount
    };
}

export function loadingDashboardData() {
    return { type: ACTIONS.LOADING_DASHBOARD_DATA };
}

export function loadedDashboardData() {
    return { type: ACTIONS.LOADED_DASHBOARD_DATA };
}

export function loadingDashboardBinderStudies() {
    return { type: ACTIONS.LOADING_DASHBOARD_BINDER_STUDIES };
}

export function loadedDashboardBinderStudies() {
    return { type: ACTIONS.LOADED_DASHBOARD_BINDER_STUDIES };
}

export function loadingDashboardBinderStudiesAdminBinders() {
    return { type: ACTIONS.LOADING_DASHBOARD_BINDER_STUDIES_ADMIN_BINDERS };
}

export function loadedDashboardBinderStudiesAdminBinders() {
    return { type: ACTIONS.LOADED_DASHBOARD_BINDER_STUDIES_ADMIN_BINDERS };
}

export function clearDashboardData() {
    return { type: ACTIONS.CLEAR_DASHBOARD_DATA };
}